import { useCallback, useState } from 'react';
import type { LabelScanResult } from '@nutrigeist/shared';
import { intakeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { CameraCapture } from './CameraCapture.js';

interface LabelScannerProps {
  onScanned: (result: LabelScanResult) => void;
  onClose: () => void;
}

/**
 * Photographs a European nutrition table and turns it into structured macros.
 *
 * Transcription, not inference: the numbers are printed on the package, and
 * the server cross-checks them against each other (kJ against kcal, macros
 * against stated energy) before offering them for confirmation.
 */
export function LabelScanner({ onScanned, onClose }: LabelScannerProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (image: Blob) => {
      setBusy(true);
      setError(null);

      try {
        onScanned(await intakeApi.scanLabel(image));
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'Das Bild konnte nicht ausgewertet werden.',
        );
      } finally {
        setBusy(false);
      }
    },
    [onScanned],
  );

  return (
    <CameraCapture
      title="Nährwerttabelle fotografieren"
      hint="Fotografiere die Tabelle formatfüllend und gerade von vorne. Vermeide Reflexionen — die Spalte „je 100 g“ muss lesbar sein."
      captureLabel="Aufnehmen"
      busyLabel="Wird ausgewertet…"
      busy={busy}
      error={error}
      onCapture={(image) => void submit(image)}
      onClose={onClose}
    />
  );
}
